import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Header } from './components/Header'
import { ClockDisplay } from './components/ClockDisplay'
import { CanthaClockDisplay } from './components/CanthaClockDisplay'
import { VirtualizedEventList } from './components/VirtualizedEventList'
import { EventsProvider, useEvents } from './store/events'
import { SettingsProvider, useSettings } from './store/settings'
import { ThemeProvider } from './store/theme'
import { CookieProvider } from './store/cookies'
import { ToastProvider } from './store/toast'
import { isEventRunning, getUpcomingOccurrences, getMostRecentStartUtc } from './utils/time'
import styles from './styles/styles.module.css'
import SVG  from './components/IcoComponents';
import type { EventDefinition, Expansion } from './types';
import { NowProvider, useNow } from './store/now'
import { Tooltip } from 'react-tooltip'
import { EventCardSkeleton } from './components/EventCardSkeleton'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { Analytics } from '@vercel/analytics/react'
const NotFoundPage = lazy(() =>
  import('./NotFoundPage').then((module) => ({
    default: module.default,
  }))
);

const FilterModal = lazy(() =>
  import('./components/FilterModal').then((module) => ({
    default: module.FilterModal,
  }))
);

const ScrollToTop = lazy(() =>
  import('./components/ScrollToTop').then((module) => ({
    default: module.ScrollToTop,
  }))
);

const CookieAcceptBar = lazy(() =>
  import('./components/CookieAcceptBar').then((module) => ({
    default: module.CookieAcceptBar,
  }))
);

const EventCardFigma = lazy(() =>
  import('./components/EventCardFigma').then((module) => ({
    default: module.EventCardFigma,
  }))
);

const expansionSortOrder: Record<Expansion, number> = {
  Core: 0,
  S1: 1,
  S2: 2,
  S3: 3,
  S4: 4,
  HoT: 5,
  PoF: 6,
  Icebrood: 7,
  EoD: 8,
  SotO: 9,
  Janthir: 10,
  VoE: 11,
};

type ScheduledOccurrence = { event: EventDefinition; start: Date };

function Lists({ onOpenSettings }: { onOpenSettings: () => void }) {
  const now = useNow();
  const { events: META_EVENTS, isLoading: eventsLoading } = useEvents();
  const [currentButtonClicked, setCurrentButtonClicked] = useState(false);
  const [upcomingButtonClicked, setUpcomingButtonClicked] = useState(false);
  const { trackedIds, sectionsCollapsed, toggleSectionCollapsed, isFavorite, favorites, scheduleLookaheadHours, dayNightTrackers } = useSettings();
  
  // Track if this is the first render to disable initial animations
  const [isFirstRender, setIsFirstRender] = useState(true);
  
  // Remount counter to force virtualizer to redraw when tracked events change
  const [virtualizerRemountKey, setVirtualizerRemountKey] = useState(0);
  
  // Animation state management
  const [animatingEvents, setAnimatingEvents] = useState<{
    appearing: Set<string>;
    disappearing: Set<string>;
  }>({ appearing: new Set(), disappearing: new Set() });
  const previousCurrentIdsRef = useRef<Set<string>>(new Set());
  const [eventsToShowDisappearing, setEventsToShowDisappearing] = useState<Array<{ event: EventDefinition; start: Date }>>([]);
  const scheduleCacheRef = useRef<Map<string, Date[]>>(new Map());
  
  // Force virtualizer remount when trackedIds changes (e.g., from filter modal)
  useEffect(() => {
    setVirtualizerRemountKey(prev => prev + 1);
  }, [trackedIds]);

  // Timer removed - now passed as prop from parent

  const filtered = useMemo(() => {
    if (eventsLoading || META_EVENTS.length === 0) {
      return [];
    }
    // Get all tracked event IDs, including grouped events
    // For each tracked event, if it's part of a group, add all events in that group
    const expandedTrackedIds = new Set(trackedIds);
    trackedIds.forEach(id => {
      const event = META_EVENTS.find(e => e.id === id);
      if (event?.eventGroup) {
        // Add all events in the same group
        META_EVENTS
          .filter(e => e.eventGroup === event.eventGroup)
          .forEach(e => expandedTrackedIds.add(e.id));
      }
    });
    
    return META_EVENTS.filter(e => expandedTrackedIds.has(e.id));
  }, [trackedIds, META_EVENTS, eventsLoading]);

  const utcMinuteKey = `${now.getUTCFullYear()}-${now.getUTCMonth()}-${now.getUTCDate()}-${now.getUTCHours()}-${now.getUTCMinutes()}`;

  const scheduleReferenceTime = useMemo(() => {
    const truncated = new Date(now);
    truncated.setUTCSeconds(0, 0);
    return truncated;
  }, [utcMinuteKey]);

  const current = useMemo(() => {
    return filtered
      .filter(ev => isEventRunning(ev, scheduleReferenceTime))
      .sort((a, b) => {
        const aIsFavorite = isFavorite(a.id);
        const bIsFavorite = isFavorite(b.id);
        if (aIsFavorite && !bIsFavorite) return -1;
        if (!aIsFavorite && bIsFavorite) return 1;

        const recentA = getMostRecentStartUtc(a, scheduleReferenceTime).getTime();
        const recentB = getMostRecentStartUtc(b, scheduleReferenceTime).getTime();
        if (recentA !== recentB) {
          return recentA - recentB;
        }

        const expansionComparison =
          (expansionSortOrder[a.expansion] ?? Number.MAX_SAFE_INTEGER) -
          (expansionSortOrder[b.expansion] ?? Number.MAX_SAFE_INTEGER);
        if (expansionComparison !== 0) {
          return expansionComparison;
        }

        const intervalA = a.intervalHours ?? 1;
        const intervalB = b.intervalHours ?? 1;
        if (intervalA !== intervalB) {
          return intervalB - intervalA;
        }

        return a.name.localeCompare(b.name);
      });
  }, [filtered, scheduleReferenceTime, favorites]);

  useEffect(() => {
    scheduleCacheRef.current.clear();
  }, [scheduleReferenceTime, scheduleLookaheadHours]);

  const getOccurrencesCached = useCallback(
    (event: EventDefinition) => {
      const key = `${event.id}-${scheduleReferenceTime.getTime()}-${scheduleLookaheadHours}`;
      const cached = scheduleCacheRef.current.get(key);
      if (cached) {
        return cached;
      }
      const occurrences = getUpcomingOccurrences(event, scheduleReferenceTime, scheduleLookaheadHours);
      scheduleCacheRef.current.set(key, occurrences);
      return occurrences;
    },
    [scheduleReferenceTime, scheduleLookaheadHours]
  );

  const upcomingOccurrences = useMemo(() => {
    const occurrences: ScheduledOccurrence[] = [];

    filtered.forEach(event => {
      const starts = getOccurrencesCached(event);
      starts.forEach(start => {
        occurrences.push({ event, start });
      });
    });

    return occurrences;
  }, [filtered, getOccurrencesCached]);

  const nowTime = scheduleReferenceTime.getTime();

  const earliestOccurrenceByEvent = useMemo(() => {
    const map = new Map<string, number>();
    upcomingOccurrences.forEach(({ event, start }) => {
      const startTime = start.getTime();
      const existing = map.get(event.id);
      if (existing === undefined || startTime < existing) {
        map.set(event.id, startTime);
      }
    });
    return map;
  }, [upcomingOccurrences]);

  const upcoming = useMemo(() => {
    const futureOccurrences = upcomingOccurrences.filter(({ start }) => start.getTime() > nowTime);

    futureOccurrences.sort((a, b) => {
      const aIsFavorite = isFavorite(a.event.id) && earliestOccurrenceByEvent.get(a.event.id) === a.start.getTime();
      const bIsFavorite = isFavorite(b.event.id) && earliestOccurrenceByEvent.get(b.event.id) === b.start.getTime();
      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;

      const timeDifference = a.start.getTime() - b.start.getTime();
      if (timeDifference !== 0) return timeDifference;

      const expansionComparison =
        (expansionSortOrder[a.event.expansion] ?? Number.MAX_SAFE_INTEGER) -
        (expansionSortOrder[b.event.expansion] ?? Number.MAX_SAFE_INTEGER);
      if (expansionComparison !== 0) return expansionComparison;

      const intervalA = a.event.intervalHours ?? 1;
      const intervalB = b.event.intervalHours ?? 1;
      if (intervalA !== intervalB) {
        return intervalB - intervalA;
      }

      return a.event.name.localeCompare(b.event.name);
    });

    return futureOccurrences;
  }, [upcomingOccurrences, earliestOccurrenceByEvent, nowTime, favorites]);

  // Calculate skeleton item count to match expected content and prevent layout shifts
  // Show enough skeleton items to match actual content (capped at viewport height for performance)
  // Viewport height ~800px / 192px per item ≈ 4-5 items visible, but show more to prevent shifts
  const MAX_SKELETON_ITEMS = 15; // Reasonable limit to prevent rendering delays
  const upcomingSkeletonCount = useMemo(() => {
    if (!upcoming.length || sectionsCollapsed.upcoming) return 5;
    // Show skeleton items matching actual count, up to reasonable limit
    return Math.min(upcoming.length, MAX_SKELETON_ITEMS);
  }, [upcoming.length, sectionsCollapsed.upcoming]);

  const currentSkeletonCount = useMemo(() => {
    const totalItems = current.length || eventsToShowDisappearing.length || 0;
    if (!totalItems || sectionsCollapsed.current) return 3;
    // Current events are usually fewer, so lower limit
    return Math.min(totalItems, 10);
  }, [current.length, eventsToShowDisappearing.length, sectionsCollapsed.current]);

  // Set first render to false after component mounts
  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  // Detect animation state changes
  useEffect(() => {
    const currentIds = new Set(current.map(ev => ev.id));
    const previousIds = previousCurrentIdsRef.current;
    
    // Skip animation detection on first render
    if (isFirstRender) {
      previousCurrentIdsRef.current = currentIds;
      return;
    }
    
    // Find newly appearing events
    const appearing = new Set<string>();
    currentIds.forEach(id => {
      if (!previousIds.has(id)) {
        appearing.add(id);
      }
    });
    
    // Find disappearing events
    const disappearing = new Set<string>();
    const disappearingEvents: Array<{ event: EventDefinition; start: Date }> = [];
    previousIds.forEach(id => {
      if (!currentIds.has(id)) {
        disappearing.add(id);
        // Find the event definition for the disappearing event
        const event = filtered.find(ev => ev.id === id);
        if (event) {
          disappearingEvents.push({ event, start: getMostRecentStartUtc(event, now) });
        }
      }
    });
    
    // Update animation state
    if (appearing.size > 0 || disappearing.size > 0) {
      setAnimatingEvents(prev => ({
        appearing: new Set([...prev.appearing, ...appearing]),
        disappearing: new Set([...prev.disappearing, ...disappearing])
      }));
      
      // Show disappearing events temporarily
      if (disappearingEvents.length > 0) {
        setEventsToShowDisappearing(disappearingEvents);
      }
      
      // Clear appearing animations after animation duration
      if (appearing.size > 0) {
        setTimeout(() => {
          setAnimatingEvents(prev => ({
            ...prev,
            appearing: new Set([...prev.appearing].filter(id => !appearing.has(id)))
          }));
        }, 300); // Match CSS animation duration
      }
      
      // Clear disappearing animations after animation duration
      if (disappearing.size > 0) {
        setTimeout(() => {
          setAnimatingEvents(prev => ({
            ...prev,
            disappearing: new Set([...prev.disappearing].filter(id => !disappearing.has(id)))
          }));
          setEventsToShowDisappearing([]);
        }, 200); // Match CSS animation duration
      }
    }
    
    // Update previous state
    previousCurrentIdsRef.current = currentIds;
  }, [current, filtered, isFirstRender, now]);

  return (
    <div className="mx-auto max-w-[700px] px-1 xxs:px-2 sm:px-4 md:px-6 lg:px-[30px] pt-[48px] pb-8">
      {/* Clock Displays - only show if respective trackers are enabled */}
      {dayNightTrackers.tyria && (
        <div className="mb-8">
          <ClockDisplay />
        </div>
      )}
      {dayNightTrackers.cantha && (
        <div className="mb-8">
          <CanthaClockDisplay />
        </div>
      )}
      
      <section 
        className={`transition-[margin-bottom] duration-150 ease-in-out ${sectionsCollapsed.current ? "mb-4" : "mb-14"}`}
        style={{ contain: 'layout' }}
      >
        <div className="flex-col mb-4">
          <h1 
            className={`${styles.newsreaderh1Bold} pb-1`}
          >
            Ongoing
          </h1>
          <button 
            onClick={() => {
              setCurrentButtonClicked(true);
              toggleSectionCollapsed('current');
              setTimeout(() => setCurrentButtonClicked(false), 200);
            }}
            className={`text-base inline-flex items-center gap-1 hover:[color:var(--color-text-default)] transition-transform duration-200 ease-in-out [color:var(--color-text-muted)] [font-family:var(--typography-newsreader-button)] ${
              currentButtonClicked ? 'scale-97' : 'hover:scale-100'
            }`}
          >
            {sectionsCollapsed.current ? 'Show' : 'Hide'}{' '}
            {sectionsCollapsed.current ? <span className="inline-flex -translate-y-0.5"><SVG.IcoShow /></span> : <SVG.IcoHideAlt />}
          </button>
        </div>
        <div 
          className={`overflow-visible transition-all duration-100 ease-in-out ${
            sectionsCollapsed.current 
              ? 'max-h-0 opacity-0 transform -translate-y-2 pointer-events-none' 
              : 'max-h-[99999px] opacity-100'
          }`}
          style={sectionsCollapsed.current ? { willChange: 'transform, opacity' } : { 
            // Prevent blurriness after transition completes
            transform: 'none',
            willChange: 'auto'
          }}
        >
          {current.length || eventsToShowDisappearing.length ? (
            <Suspense fallback={
              <div className="grid gap-3">
                {Array.from({ length: currentSkeletonCount }).map((_, i) => (
                  <EventCardSkeleton key={`skeleton-current-${i}`} delay={i * 10} />
                ))}
              </div>
            }>
              <VirtualizedEventList
                key={`current-${sectionsCollapsed.current ? 'hidden' : 'visible'}-${current.length}-remount-${virtualizerRemountKey}`}
                items={current}
                renderItem={(ev) => {
                  const recentStart = getMostRecentStartUtc(ev, now);
                  return (
                    <EventCardFigma 
                      key={ev.id} 
                      ev={ev} 
                      scheduleStart={recentStart}
                      isRunning
                      isAnimatingIn={animatingEvents.appearing.has(ev.id)}
                      isAnimatingOut={false}
                    />
                  );
                }}
                estimateSize={180}
                overscan={3}
                enabled={current.length > 10 && !sectionsCollapsed.current}
                isVisible={!sectionsCollapsed.current}
              />
              {/* Always render disappearing events (usually few) */}
              {eventsToShowDisappearing.length > 0 && (
                <div className="grid gap-3">
                  {eventsToShowDisappearing.map(({ event, start }) => (
                    <EventCardFigma 
                      key={`disappearing-${event.id}`} 
                      ev={event} 
                      scheduleStart={start}
                      isAnimatingIn={false}
                      isAnimatingOut={true}
                    />
                  ))}
                </div>
              )}
            </Suspense>
          ) : (
            <div className="flex flex-col">
              <div className={styles.NewsreaderBodyM}>
                There are no active events at the moment
              </div>
              <div className={`${styles.inconsolataBodyS} mt-1`}>
              Try changing your tracking{' '}
              <button 
                onClick={onOpenSettings}
                className="underline hover:no-underline cursor-pointer [color:var(--color-text-muted)] inline-flex items-center gap-1"
              >
                settings
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline mt-0.5 -ml-0.5"
                >
                  <path 
                    d="M9.75 2.25L2.25 9.75M2.25 2.25H9.75V9.75" 
                    stroke="currentColor" 
                    strokeWidth="1" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              </div>
            </div>
          )}
        </div>
      </section>
      
      <section 
        className={`transition-[margin-bottom] duration-100 ease-in-out ${sectionsCollapsed.upcoming ? "mb-4" : "mb-14"}`}
        style={{ contain: 'layout' }}
      >
        <div className="flex-col mb-4">
          <h1 
            className={`${styles.newsreaderh1Bold} pb-1`}
          >
            Upcoming
          </h1>
          <button
            onClick={() => {
              setUpcomingButtonClicked(true);
              toggleSectionCollapsed('upcoming');
              setTimeout(() => setUpcomingButtonClicked(false), 200);
            }}
            className={`
              text-base
              inline-flex
              items-center
              gap-1 
              hover:[color:var(--color-text-default)] 
              transition-transform duration-200 ease-in-out
              [color:var(--color-text-muted)] 
              [font-family:var(--typography-newsreader-button)]
              ${upcomingButtonClicked ? 'scale-97' : 'hover:scale-100'}
            `}
          >
              {sectionsCollapsed.upcoming ? 'Show' : 'Hide'}{' '}
              {sectionsCollapsed.upcoming ? <span className="inline-flex -translate-y-0.5"><SVG.IcoShow /></span> : <SVG.IcoHideAlt />}
          </button>
        </div>
        <div 
          className={`overflow-visible transition-all duration-100 ease-in-out ${
            sectionsCollapsed.upcoming 
              ? 'max-h-0 opacity-0 transform -translate-y-2 pointer-events-none' 
              : 'max-h-[99999px] opacity-100'
          }`}
          style={sectionsCollapsed.upcoming ? { willChange: 'transform, opacity' } : { 
            // Prevent blurriness after transition completes
            transform: 'none',
            willChange: 'auto'
          }}
        >
          {upcoming.length ? (
            <Suspense fallback={
              <div className="grid gap-3">
                {Array.from({ length: upcomingSkeletonCount }).map((_, i) => (
                  <EventCardSkeleton key={`skeleton-upcoming-${i}`} delay={i * 10} />
                ))}
              </div>
            }>
              <VirtualizedEventList
                key={`upcoming-${sectionsCollapsed.upcoming ? 'hidden' : 'visible'}-${upcoming.length}-remount-${virtualizerRemountKey}`}
                items={upcoming}
                renderItem={({ event, start }) => (
                  <EventCardFigma 
                    key={`${event.id}-${start.toISOString()}`} 
                    ev={event} 
                    scheduleStart={start}
                    context="upcoming"
                    isAnimatingIn={false}
                    isAnimatingOut={false}
                  />
                )}
                estimateSize={180}
                overscan={3}
                enabled={false}
                isVisible={!sectionsCollapsed.upcoming}
              />
            </Suspense>
          ) : (
            <div className="flex flex-col">
              <div className={styles.NewsreaderBodyM}>
                Whoa! Hold your horses, cowboy!🤠<br /> Looks like you've <u>turned off</u> tracking for <em><b>all</b></em> of the events!
              </div>
              <div className={`${styles.inconsolataBodyS} mt-1`}>
              Try changing your tracking{' '}
              <button 
                onClick={onOpenSettings}
                className="underline hover:no-underline cursor-pointer [color:var(--color-text-muted)] inline-flex items-center gap-1"
              >
                settings
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline mt-0.5 -ml-0.5"
                >
                  <path 
                    d="M9.75 2.25L2.25 9.75M2.25 2.25H9.75V9.75" 
                    stroke="currentColor" 
                    strokeWidth="1" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Tooltip
        border="1px solid var(--color-fg-secondary)"
        id="expansion-tooltip"
        className="z-9999"
        place="left"
        delayShow={300}
        delayHide={100}
        offset={16}
        float
        style={{
          backgroundColor: 'var(--color-fg-secondary)',
          color: 'var(--color-text-emphasis)',
          fontSize: '12px',
          fontFamily: 'var(--font-family-inconsolata)',
          padding: '6px 8px 4px 8px',
          boxShadow: 'var(--shadow-button)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
    </div>
  )
}

function Shell() {
  const [open, setOpen] = useState(false);
  const { isLoading: eventsLoading } = useEvents();

  const handleCookieAccept = () => {
    // Cookie consent has been accepted
    console.log('Cookie consent accepted');
  };

  const handleLearnMore = () => {
    // Open a modal or navigate to privacy policy
    // For now, just log - you can implement this later
    console.log('Learn more about cookies clicked');
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header onOpenSettings={() => setOpen(true)} modalOpen={open} />
      <main className="flex-1">
        {!eventsLoading && <Lists onOpenSettings={() => setOpen(true)} />}
      </main>
      <Suspense fallback={null}>
        <FilterModal open={open} onClose={() => setOpen(false)} />
      </Suspense>
      <Suspense fallback={null}>
        <ScrollToTop />
      </Suspense>
      <Suspense fallback={null}>
        <CookieAcceptBar onAccept={handleCookieAccept} onLearnMore={handleLearnMore} />
      </Suspense>
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <CookieProvider>
        <EventsProvider>
          <SettingsProvider>
            <ToastProvider>
              <NowProvider>
                <BrowserRouter>
                  <Suspense fallback={null}>
                    <Routes>
                      <Route path="/" element={<Shell />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </NowProvider>
            </ToastProvider>
          </SettingsProvider>
        </EventsProvider>
      </CookieProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  )
}

export default App
